import React, { useState } from "react";
import "../../../../../css/CashflowModeller2/DialogBoxes/DialogBoxes.css";
import Close from "../Buttons/Close";
import Input2 from "../Inputs/Input2";
import Input1 from "../Inputs/Input1";
import AddButton from "../Buttons/AddButton";
import InputSmall from "../Inputs/InputSmall";
import RemoveButton from "../Buttons/RemoveButton";
import { useCfmContext } from "../../Context/CfmContext";
import getInvestmentOptions from "../../Utils/InvestmentOptions";
import useDraggableDialog from "./Utils/UseDraggableDialog";

const defaultRow = {
  sources: [],
  amount: "",
  start: "",
  end: "",
};

/**
 * RegularIncomeDialogBox component allows users to add, edit, and remove regular income sources
 * with validation for age ranges and amount. It displays a draggable dialog box with input fields
 * for source, amount, start age, and end age, and manages its own local state as well as updates
 * the global context.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {Function} props.onClose - Callback function to close the dialog box.
 *
 * @returns {JSX.Element} The rendered RegularIncomeDialogBox component.
 *
 * @example
 * <RegularIncomeDialogBox onClose={() => setShowDialog(false)} />
 */
const RegularIncomeDialogBox = ({ onClose }) => {
  const { state, dispatch } = useCfmContext();
  const [inputRow, setInputRow] = useState({ ...defaultRow });
  const [rows, setRows] = useState(state.regularIncome || []);
  const [rowErrors, setRowErrors] = useState([]);
  const [startEndError, setStartEndError] = useState(false);
  const [inputKey, setInputKey] = useState(Date.now()); // used to force reset Input2
  const regularIncomeOptions = getInvestmentOptions(state);
  const { ref, position, handleMouseDown } = useDraggableDialog();
  

  /**
   * Handles changes to input fields by updating the corresponding field in the inputRow state.
   * Optionally, can validate the "start" and "end" fields to ensure start is not greater than end.
   *
   * @param {string} field - The name of the field being updated.
   * @param {string|number} value - The new value for the specified field.
   */
  const handleInputChange = (field, value) => {
    setInputRow((prev) => ({ ...prev, [field]: value }));

    // if (field === "start" || field === "end") {
    //   const start = field === "start" ? value : inputRow.start;
    //   const end = field === "end" ? value : inputRow.end;
    //   setStartEndError(Number(start) > Number(end));
    // }
  };

  /**
   * Updates the local state and dispatches an action to update the regular income context.
   *
   * @param {Array<Object>} updatedRows - The updated array of regular income rows.
   * @returns {void}
   */
  const updateContext = (updatedRows) => {
    setRows(updatedRows);
    dispatch({ type: "UPDATE_REGULAR_INCOME", payload: updatedRows });
  };

  
  /**
   * Handles adding a new income row to the list.
   * 
   * Validates the input row for required sources, ensures the start and end ages are valid (start <= end and both >= 18),
   * and prevents adding invalid rows. If valid, it maps each selected source to a new row object, updates the context
   * with the new rows, resets error states, and clears the input row for the next entry.
   *
   * Side Effects:
   * - Updates the rows context with new entries.
   * - Updates row-level error states.
   * - Resets the input row and related UI state.
   *
   * @function
   * @returns {void}
   */
  const handleAdd = () => {
    if (!inputRow.sources?.length) return;

    const start = Number(inputRow.start);
    const end = Number(inputRow.end);

    const hasRangeError = start > end;
    const tooYoung = start < 18 || end < 18;

    if (hasRangeError || tooYoung) {
      setStartEndError(true);
      return; // prevent adding invalid row
    }

    // valid, add the row(s)
    const newRows = inputRow.sources.map((source) => {
      const [fullName, type] = source.split(" - ").map((s) => s.trim());
      return {
        name: fullName,
        type,
        amount: inputRow.amount,
        start: inputRow.start,
        end: inputRow.end,
      };
    });

    const updated = [...rows, ...newRows];
    updateContext(updated);

    const newErrors = newRows.map(() => false); // valid rows, no row-level error
    setRowErrors([...rowErrors, ...newErrors]);

    setInputRow({ ...defaultRow });
    setInputKey(Date.now()); // force reset source input
    setStartEndError(false); // clear error once valid
  };

  /**
   * Handles changes to a specific row in the data table.
   *
   * Updates the specified field of a row at the given index with the new value.
   * If the changed field is "start" or "end", performs validation to ensure:
   *   - The start value is not greater than the end value.
   *   - Neither start nor end is less than 18.
   * Updates the row error state accordingly and propagates the updated rows to context.
   *
   * @param {number} index - The index of the row being updated.
   * @param {string} field - The field in the row to update ("start", "end", or other fields).
   * @param {string|number} value - The new value to set for the specified field.
   */
  const handleRowChange = (index, field, value) => {
    if (value === "") return;

    const updated = [...rows];
    updated[index][field] = value;

    if (field === "start" || field === "end") {
      const start = Number(field === "start" ? value : updated[index].start);
      const end = Number(field === "end" ? value : updated[index].end);
      const hasRangeError = start > end;
      const tooYoung = start < 18 || end < 18;
      const errorArray = [...rowErrors];
      errorArray[index] = hasRangeError || tooYoung;
      setRowErrors(errorArray);
    }

    updateContext(updated);
  };

  /**
   * Removes a row and its corresponding error at the specified index.
   *
   * @param {number} index - The index of the row to remove.
   * @returns {void}
   *
   * Updates the context with the new rows array and updates the row errors state.
   */
  const handleRemove = (index) => {
    const updatedRows = rows.filter((_, i) => i !== index);
    const updatedErrors = rowErrors.filter((_, i) => i !== index);
    updateContext(updatedRows);
    setRowErrors(updatedErrors);
  };

  const isAddDisabled =
    !inputRow.sources.length ||
    !inputRow.amount ||
    !inputRow.start ||
    !inputRow.end;

  return (
    <div
      ref={ref}
      className="dialog-box"
      style={{
        position: "absolute",
        left: `${position.x}px`,
        top: `${position.y}px`,
      }}
    >
      <div
        className="dialog-header"
        onMouseDown={handleMouseDown}
        style={{ cursor: "grab" }}
      >
        <div className="Title">Investment Income</div>
        <div className="dialog-header-buttons">
          <div className="Content-Small" onClick={onClose}>
            Close
          </div>
          <div onClick={onClose}>
            <Close />
          </div>
        </div>
      </div>

      <div className="dialog-row dialog-row-inputs">
        <Input2
          key={inputKey} // force remount on reset
          label="Source"
          value={
            inputRow.sources?.length
              ? inputRow.sources
                  .map((src) => {
                    const parts = src.trim().split(" ");
                    return `${parts[0]} - ${parts.at(-1)}`;
                  })
                  .join(", ")
              : ""
          }
          onChange={(val) => handleInputChange("sources", val)}
          options={regularIncomeOptions}
          isCheckBoxOptions={true}
        />
        <Input1
          currency={true}
          label="Amount"
          type="number"
          value={inputRow.amount}
          onChange={(val) => handleInputChange("amount", val)}
        />
        <InputSmall
          label="Start"
          value={inputRow.start}
          onChange={(val) => handleInputChange("start", val)}
          isError={startEndError}
        />
        <InputSmall
          label="End"
          value={inputRow.end}
          onChange={(val) => handleInputChange("end", val)}
          isError={startEndError}
        />
        <AddButton onClick={handleAdd} isDisabled={isAddDisabled} />
      </div>

      {startEndError && <div className="error Label">Invalid age range.</div>}

      {rows.map((row, index) => (
        <React.Fragment key={index}>
          <div className="no-dovider-dialogBox" />
          <div className="dialog-row dialog-row-inputs">
            <Input2
              value={`${row.name.split(" ")[0]} - ${row.type}`}
              onChange={() => {}}
              options={regularIncomeOptions}
              readOnly
            />
            <Input1
              currency={true}
              value={row.amount}
              type="number"
              onChange={(val) => handleRowChange(index, "amount", val)}
              // readOnly={true}
            />
            <InputSmall
              value={row.start}
              onChange={(val) => handleRowChange(index, "start", val)}
              isError={rowErrors[index]}
              // readOnly={true}
            />
            <InputSmall
              value={row.end}
              onChange={(val) => handleRowChange(index, "end", val)}
              isError={rowErrors[index]}
              // readOnly={true}
            />
            <RemoveButton onClick={() => handleRemove(index)} />
          </div>
          {rowErrors[index] && (
            <div className="error Label">Age must be 18 or older</div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default RegularIncomeDialogBox;
